import { useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Building2, Heart, Search, LogOut, User, LifeBuoy } from 'lucide-react';

export default function CustomerLayout() {
  const { customer, loading, logout, isCustomer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !isCustomer) {
      navigate('/customer/login');
    }
  }, [loading, isCustomer, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-12 h-12 text-sky-500 animate-pulse mx-auto mb-4" />
          <p className="text-navy-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!customer) return null;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-navy-50" data-testid="customer-layout">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-navy-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <Building2 className="w-8 h-8 text-sky-500" />
              <span className="text-xl font-heading font-bold text-navy-900">EstateCloud</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/search">
                <Button variant="ghost" className="gap-2">
                  <Search className="w-4 h-4" />
                  Search
                </Button>
              </Link>
              <Link to="/customer/favorites">
                <Button
                  variant={location.pathname === '/customer/favorites' ? 'default' : 'outline'}
                  className={location.pathname === '/customer/favorites' ? 'bg-sky-500' : ''}
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Favorites
                </Button>
              </Link>
              <Link to="/customer/tickets">
                <Button
                  variant={location.pathname.startsWith('/customer/tickets') ? 'default' : 'outline'}
                  className={location.pathname.startsWith('/customer/tickets') ? 'bg-sky-500' : ''}
                >
                  <LifeBuoy className="w-4 h-4 mr-2" />
                  Support
                </Button>
              </Link>
              <Link to="/customer/profile" className="flex items-center gap-2 text-navy-600 hover:text-navy-900 transition-colors">
                <User className="w-4 h-4" />
                <span className="font-medium">{customer.name}</span>
              </Link>
              <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main>
        <Outlet />
      </main>
    </div>
  );
}
